import React, { useEffect, useState } from 'react';
import { PageHeader, StatCard, TaskCard, EmptyState, PriorityBadge, StageBadge } from '@/components/primitives';
import { api } from '@/lib/api';
import { fmtDate } from '@/lib/format';
import { Users, Phone, Video, ClipboardList, ListChecks, AlertOctagon, Sparkles, Building2 } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

export default function DashboardPage() {
  const [scope, setScope] = useState('week');
  const [summary, setSummary] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/dashboard/summary?scope=${scope}`).then((r) => setSummary(r.data));
    api.get('/dashboard/candidates').then((r) => setCandidates(r.data));
  }, [scope]);

  if (!summary) return null;

  const pipelineData = Object.entries(summary.pipeline || {}).map(([k, v]) => ({ stage: k, count: v }));

  return (
    <>
      <PageHeader
        title="Command Dashboard"
        description="Every candidate, follow-up and priority across all your client companies."
        actions={
          <Tabs value={scope} onValueChange={setScope}>
            <TabsList data-testid="dashboard-scope">
              <TabsTrigger value="today" data-testid="scope-today">Today</TabsTrigger>
              <TabsTrigger value="week" data-testid="scope-week">This week</TabsTrigger>
              <TabsTrigger value="month" data-testid="scope-month">This month</TabsTrigger>
            </TabsList>
          </Tabs>
        }
      />

      <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <StatCard label="Candidates" value={summary.kpis.total_candidates} hint="across all companies" icon={Users} tone="primary" />
          <StatCard label="In pipeline" value={summary.kpis.in_pipeline} icon={ListChecks} />
          <StatCard label="Tasks due" value={summary.kpis.tasks_due} hint="in scope" icon={ClipboardList} tone="good" />
          <StatCard label="Tasks overdue" value={summary.kpis.tasks_overdue} icon={AlertOctagon} tone={summary.kpis.tasks_overdue ? 'danger' : 'default'} />
          <StatCard label="Calls today" value={summary.kpis.calls_today} icon={Phone} />
          <StatCard label="Interviews today" value={summary.kpis.interviews_today} icon={Video} />
          <StatCard label="Onboardings" value={summary.kpis.onboardings_active} hint="in progress" icon={ClipboardList} />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Upcoming tasks & follow-ups</h2>
            {summary.tasks.length === 0 ? (
              <EmptyState icon={ClipboardList} title="You're all caught up" description="Add candidates or move them along - follow-ups will show up here." />
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {summary.tasks.slice(0, 12).map((t) => (
                  <TaskCard key={`${t.entity_type}-${t.id}`} task={t} onClick={() => {
                    if (t.entity_type === 'candidate') navigate(`/companies/${t.company_id}/recruitment?open=${t.id}`);
                    else navigate(`/companies/${t.company_id}/onboarding?open=${t.id}`);
                  }} />
                ))}
              </div>
            )}
          </div>
          <div className="space-y-4">
            <h2 className="font-display text-lg font-semibold">Pipeline distribution</h2>
            <div className="rounded-xl border bg-card p-4 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pipelineData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="stage" tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => v[0].toUpperCase() + v.slice(1)} />
                  <YAxis tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, borderColor: 'hsl(var(--border))', background: 'hsl(var(--card))', fontSize: 12 }} />
                  <Bar dataKey="count" fill="hsl(var(--chart-1))" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold">All candidates</h2>
            <Button variant="outline" onClick={() => navigate('/companies')} data-testid="nav-companies-cta">
              <Building2 className="h-4 w-4 mr-2" /> Manage companies
            </Button>
          </div>
          <div className="rounded-xl border bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Next action</TableHead>
                  <TableHead>Follow-up</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {candidates.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">No candidates yet - create a company to get started.</TableCell></TableRow>
                )}
                {candidates.map((c) => (
                  <TableRow key={c.id} className="cursor-pointer" onClick={() => navigate(`/companies/${c.company_id}/recruitment?open=${c.id}`)} data-testid={`dashboard-candidate-row-${c.id}`}>
                    <TableCell>
                      <p className="font-medium">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.email}</p>
                    </TableCell>
                    <TableCell><span className="text-sm">{c.company_name}</span></TableCell>
                    <TableCell><span className="text-sm">{c.role_applied}</span></TableCell>
                    <TableCell><StageBadge stage={c.stage} /></TableCell>
                    <TableCell><PriorityBadge priority={c.priority} /></TableCell>
                    <TableCell><span className="capitalize text-sm">{c.next_action || '—'}</span></TableCell>
                    <TableCell>{fmtDate(c.next_followup_date)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </>
  );
}
